import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Input,
  Select,
  Card,
  Typography,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Spin,
  AutoComplete,
} from 'antd';
import {
  DeleteOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { supportTicketAPI, userAPI } from '../api';
import AdminSupportTicketDialog from './AdminSupportTicketDialog';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const SupportTicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // 搜索和过滤参数
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dropshipperFilter, setDropshipperFilter] = useState(null);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionMap, setUserOptionMap] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [pagination.current, pagination.pageSize, searchText, statusFilter, dropshipperFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
      };
      
      if (searchText) params.search = searchText;
      if (statusFilter) params.status = statusFilter;
      if (dropshipperFilter) params.dropshipper_id = dropshipperFilter;

      const response = await supportTicketAPI.getAll(params);
      // 按创建时间降序排序，最晚创建的排在最上面
      const sortedTickets = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTickets(sortedTickets);
      
      // Note: Backend doesn't return total count, so we estimate
      // In a real implementation, the backend should return total count
      setPagination(prev => ({
        ...prev,
        total: sortedTickets.length === pagination.pageSize 
          ? (pagination.current * pagination.pageSize) + 1 
          : (pagination.current - 1) * pagination.pageSize + sortedTickets.length
      }));
    } catch (error) {
      message.error('获取支持工单列表失败');
      
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (value) => {
    const query = (value || '').trim();
    setUserSearchInput(value);
    if (!query) {
      setUserOptions([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const response = await userAPI.getAll({
        page: 1,
        page_size: 20,
        role: 'dropshipper',
        search: query,
      });
      const results = (response.data?.data || []).filter(user => user.role === 'dropshipper');
      setUserOptions(results);
      setUserOptionMap(prev => {
        const next = { ...prev };
        results.forEach(user => {
          next[user.id] = user;
        });
        return next;
      });
    } catch (error) {
      message.error('搜索用户失败');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await supportTicketAPI.delete(ticketId);
      message.success('支持工单删除成功');
      fetchTickets();
    } catch (error) {
      message.error('删除支持工单失败');
      
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
    setSelectedTicket(null);
    fetchTickets(); // 刷新列表以更新状态
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDropshipperFilter = (value) => {
    setDropshipperFilter(value || null);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleReset = () => {
    setSearchText('');
    setStatusFilter('');
    setDropshipperFilter(null);
    setUserSearchInput('');
    setUserOptions([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatUserLabel = (user) =>
    `${user.username}${user.email ? ` (${user.email})` : ''}`;

  const userSelectOptions = useMemo(() => {
    const base = userOptions || [];
    const filterId = dropshipperFilter ? Number(dropshipperFilter) : null;
    const extended =
      filterId && !base.some(user => user?.id === filterId)
        ? [...base, userOptionMap[filterId]].filter(Boolean)
        : base;
    return extended.map(user => ({
      value: String(user.id),
      label: formatUserLabel(user),
    }));
  }, [userOptions, userOptionMap, dropshipperFilter]);

  const columns = [
    {
      title: '工单标题',
      dataIndex: 'title',
      key: 'title',
      width: '22%',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>,
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: '客户',
      dataIndex: 'dropshipper',
      key: 'dropshipper',
      width: '18%',
      ellipsis: true,
      render: (dropshipper) => (
        <Space>
          <UserOutlined />
          <span>{dropshipper?.username || '未知'}</span>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => (
        <Tag 
          color={status === 'closed' ? 'default' : 'green'}
          icon={status === 'closed' ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
        >
          {status === 'closed' ? '已关闭' : '处理中'}
        </Tag>
      ),
      filters: [
        { text: '处理中', value: 'open' },
        { text: '已关闭', value: 'closed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '消息数',
      dataIndex: 'comments_count',
      key: 'comments_count',
      width: '10%',
      render: (count) => count ?? 0,
      sorter: (a, b) => (a.comments_count ?? 0) - (b.comments_count ?? 0),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '14%',
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: '最后活动',
      dataIndex: 'latest_comment_at',
      key: 'latest_comment_at',
      width: '14%',
      render: (date) => date ? new Date(date).toLocaleString() : '无',
      sorter: (a, b) => {
        const dateA = a.latest_comment_at ? new Date(a.latest_comment_at) : new Date(0);
        const dateB = b.latest_comment_at ? new Date(b.latest_comment_at) : new Date(0);
        return dateA - dateB;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <Tooltip title="删除支持工单">
          <Popconfirm
            title="确定要删除这个支持工单吗？"
            description="此操作无法撤销，将删除所有对话和附件。"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteTicket(record.id);
            }}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0, marginBottom: 16 }}>支持工单管理</Title>
          
          {/* Search and Filter Controls */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="按工单标题搜索"
                enterButton={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="按状态筛选"
                value={statusFilter}
                onChange={handleStatusFilter}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="open">处理中</Option>
                <Option value="closed">已关闭</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <AutoComplete
                value={userSearchInput}
                options={userSelectOptions}
                onSelect={(value, option) => {
                  handleDropshipperFilter(value);
                  setUserSearchInput(option?.label ?? '');
                }}
                onChange={(value, option) => {
                  setUserSearchInput(value);
                  if (!value) {
                    setUserOptions([]);
                    handleDropshipperFilter(null);
                  } else if (!option) {
                    handleDropshipperFilter(null);
                  }
                }}
                notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
                style={{ width: '100%' }}
                filterOption={false}
              >
                <Search
                  placeholder="筛选用户"
                  enterButton={<SearchOutlined />}
                  allowClear
                  loading={userSearchLoading}
                  onSearch={handleUserSearch}
                />
              </AutoComplete>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={fetchTickets}
                >
                  刷新
                </Button>
                <Button 
                  icon={<FilterOutlined />}
                  onClick={handleReset}
                >
                  重置筛选
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={tickets}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `显示第 ${range[0]}-${range[1]} 条，共 ${total} 个支持工单`,
            pageSizeOptions: ['20', '50', '100'],
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleViewTicket(record),
            style: { cursor: 'pointer' }
          })}
          size="middle"
        />
      </Card>

      {/* Support Ticket Dialog */}
      {dialogVisible && selectedTicket && (
        <AdminSupportTicketDialog
          ticket={selectedTicket}
          visible={dialogVisible}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default SupportTicketManagement;
